import React from 'react';
import { render, fireEvent } from '@testing-library/react';
import { PlaylistSeriesArtToolbar, ConnectServiceTypeModal } from './playlist_series_art_toolbar';

// Modal.insertModalElement mounts to a global DOM node — we don't need
// real modal semantics for these unit tests, so we short-circuit it and
// capture the payload for assertions.
jest.mock('../../global/modal/modal', () => {
    return {
        __esModule: true,
        default: {
            insertModalElement: jest.fn(),
            dismissModal: jest.fn(),
        },
    };
});
import Modal from '../../global/modal/modal';

function renderToolbar(props) {
    const defaultProps = {
        playlist: {
            id: 7,
            series_art_enabled: false,
            series_art_source: null,
            series_art_service_type_id: null,
            series_art_uploaded_filename: null,
        },
        series_art_service_types: [],
        loading_series_art_service_types: false,
        updating_playlist_series_art: false,
        uploading_playlist_series_art: false,
        onFetchServiceTypes: jest.fn(),
        onUpdatePlaylistSeriesArt: jest.fn(),
        onUploadPlaylistSeriesArt: jest.fn(),
    };
    return render(<PlaylistSeriesArtToolbar {...defaultProps} {...props} />);
}

describe('PlaylistSeriesArtToolbar (#482)', () => {
    beforeEach(() => {
        Modal.insertModalElement.mockClear();
        Modal.dismissModal.mockClear();
    });

    it('prefetches service types on mount', () => {
        const onFetchServiceTypes = jest.fn();
        renderToolbar({ onFetchServiceTypes });
        expect(onFetchServiceTypes).toHaveBeenCalledTimes(1);
    });

    it('renders the "Show series art" checkbox off by default with Connect... and Upload... buttons', () => {
        const { container } = renderToolbar();
        expect(container.querySelector('#playlist_series_art_toggle').checked).toBe(false);
        expect(container.querySelector('#playlist_series_art_connect_button')).not.toBeNull();
        expect(container.querySelector('#playlist_series_art_upload_button')).not.toBeNull();
        expect(container.querySelector('#playlist_series_art_disconnect_button')).toBeNull();
        expect(container.querySelector('#playlist_series_art_remove_button')).toBeNull();
    });

    it('toggling the checkbox dispatches update with the new enabled value', () => {
        const onUpdatePlaylistSeriesArt = jest.fn();
        const { container } = renderToolbar({ onUpdatePlaylistSeriesArt });
        fireEvent.click(container.querySelector('#playlist_series_art_toggle'));
        expect(onUpdatePlaylistSeriesArt).toHaveBeenCalledWith(7, { series_art_enabled: true });
    });

    it('replaces Connect/Upload with a Disconnect button and the service-type name once connected', () => {
        const { container, getByText } = renderToolbar({
            playlist: {
                id: 7,
                series_art_enabled: true,
                series_art_source: 'pco',
                series_art_service_type_id: 12,
            },
            series_art_service_types: [{ id: 12, name: 'Sunday Service' }],
        });
        expect(container.querySelector('#playlist_series_art_connect_button')).toBeNull();
        expect(container.querySelector('#playlist_series_art_upload_button')).toBeNull();
        expect(container.querySelector('#playlist_series_art_disconnect_button')).not.toBeNull();
        expect(getByText('Sunday Service')).not.toBeNull();
    });

    it('replaces Connect/Upload with the filename and a Remove button once an upload is set', () => {
        const { container, getByText } = renderToolbar({
            playlist: {
                id: 7,
                series_art_enabled: true,
                series_art_source: 'upload',
                series_art_uploaded_filename: 'winter-series.jpg',
            },
        });
        expect(container.querySelector('#playlist_series_art_connect_button')).toBeNull();
        expect(container.querySelector('#playlist_series_art_upload_button')).toBeNull();
        expect(container.querySelector('#playlist_series_art_remove_button')).not.toBeNull();
        expect(getByText('winter-series.jpg')).not.toBeNull();
    });

    it('Disconnect clears source + service_type_id via update', () => {
        const onUpdatePlaylistSeriesArt = jest.fn();
        const { container } = renderToolbar({
            playlist: {
                id: 7,
                series_art_enabled: true,
                series_art_source: 'pco',
                series_art_service_type_id: 12,
            },
            onUpdatePlaylistSeriesArt,
        });
        fireEvent.click(container.querySelector('#playlist_series_art_disconnect_button'));
        expect(onUpdatePlaylistSeriesArt).toHaveBeenCalledWith(7, {
            series_art_source: '',
            series_art_service_type_id: '',
        });
    });

    it('Remove clears source + uploaded url + filename via update', () => {
        const onUpdatePlaylistSeriesArt = jest.fn();
        const { container } = renderToolbar({
            playlist: {
                id: 7,
                series_art_enabled: true,
                series_art_source: 'upload',
                series_art_uploaded_filename: 'winter-series.jpg',
            },
            onUpdatePlaylistSeriesArt,
        });
        fireEvent.click(container.querySelector('#playlist_series_art_remove_button'));
        expect(onUpdatePlaylistSeriesArt).toHaveBeenCalledWith(7, {
            series_art_source: '',
            series_art_uploaded_url: '',
            series_art_uploaded_filename: '',
        });
    });

    it('opens the Connect modal with the fetched service types when Connect... is clicked', () => {
        const types = [{ id: 1, name: 'Weekend' }, { id: 2, name: 'Wednesday' }];
        const { container } = renderToolbar({ series_art_service_types: types });
        fireEvent.click(container.querySelector('#playlist_series_art_connect_button'));
        expect(Modal.insertModalElement).toHaveBeenCalledTimes(1);
        const passedElement = Modal.insertModalElement.mock.calls[0][0];
        expect(passedElement.props.serviceTypes).toEqual(types);
    });

    it('Upload... triggers the file picker and uploadPlaylistSeriesArt on file pick', () => {
        const onUploadPlaylistSeriesArt = jest.fn();
        const { container } = renderToolbar({ onUploadPlaylistSeriesArt });
        const input = container.querySelector('input[type="file"]');
        expect(input).not.toBeNull();
        const file = new File(['data'], 'series.jpg', { type: 'image/jpeg' });
        Object.defineProperty(input, 'files', { value: [file] });
        fireEvent.change(input);
        expect(onUploadPlaylistSeriesArt).toHaveBeenCalledTimes(1);
        expect(onUploadPlaylistSeriesArt.mock.calls[0][0]).toBe(7);
        expect(onUploadPlaylistSeriesArt.mock.calls[0][1]).toBe(file);
    });

    it('disables interactive controls while an update is in flight', () => {
        const { container } = renderToolbar({ updating_playlist_series_art: true });
        expect(container.querySelector('#playlist_series_art_toggle').disabled).toBe(true);
        expect(container.querySelector('#playlist_series_art_connect_button').disabled).toBe(true);
        expect(container.querySelector('#playlist_series_art_upload_button').disabled).toBe(true);
    });
});

describe('ConnectServiceTypeModal (#482)', () => {
    it('shows an empty-state hint when no service types are connected', () => {
        const { getByText } = render(
            <ConnectServiceTypeModal serviceTypes={[]} onPick={() => {}} />
        );
        expect(getByText(/No Planning Center service types are connected/i)).not.toBeNull();
    });

    it('picks a service type by id when a row is clicked', () => {
        const onPick = jest.fn();
        const { getByText } = render(
            <ConnectServiceTypeModal
                serviceTypes={[{ id: 3, name: 'Contemporary' }, { id: 4, name: 'Classic' }]}
                onPick={onPick}
            />
        );
        fireEvent.click(getByText('Classic'));
        expect(onPick).toHaveBeenCalledWith(4);
    });
});
